import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";
import { Plus, FolderSimple, Trash, ArrowRight } from "@phosphor-icons/react";
import { useT } from "../lib/i18n";
import ProjectLimitNotice from "../components/ProjectLimitNotice";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limitNoticeOpen, setLimitNoticeOpen] = useState(false);
  const t = useT();
  const nav = useNavigate();
  const planTier = user?.workspace_plan_tier || user?.plan_tier || "legacy";
  const projectLimit = { freelance: 1, business: 5, enterprise: null }[planTier] ?? null;
  const limitReached = projectLimit !== null && projects.length >= projectLimit;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function remove(id) {
    if (!window.confirm(t("dash.confirm_delete"))) return;
    await api.delete(`/projects/${id}`);
    load();
  }

  function startProjectCreation() {
    if (limitReached) {
      setLimitNoticeOpen(true);
      return;
    }
    nav("/projects/new");
  }

  return (
    <AppShell>
      <div className="flex flex-col items-start justify-between gap-4 mb-8 sm:flex-row sm:items-end">
        <div>
          <div className="text-xs font-mono text-zinc-500 uppercase tracking-wider">{t("dash.workspace")}</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t("dash.title")}</h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--text-2)]">{t("dash.sub")}</p>
          {!loading && projectLimit !== null && (
            <p className="mt-2 text-sm text-[var(--text-2)]" data-testid="project-usage">
              {projects.length} {t(projects.length === 1 ? "plan.project_one" : "plan.project_many")}
              {" / "}
              {projectLimit} {t(projectLimit === 1 ? "plan.project_one" : "plan.project_many")}
            </p>
          )}
        </div>
        {limitReached ? (
          <button type="button" onClick={() => setLimitNoticeOpen(true)} data-testid="new-project-btn" className="btn-primary inline-flex items-center gap-2 px-4 py-2">
            <Plus size={16} weight="bold" /> {t("dash.new")}
          </button>
        ) : (
          <Link to="/projects/new" aria-disabled={loading} data-testid="new-project-btn" className={`btn-primary inline-flex items-center gap-2 px-4 py-2 ${loading ? "pointer-events-none opacity-60" : ""}`}>
            <Plus size={16} weight="bold" /> {t("dash.new")}
          </Link>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">{t("dash.loading")}</div>
      ) : projects.length === 0 ? (
        <div className="grid-card p-12 text-center">
          <FolderSimple size={48} className="mx-auto text-zinc-300" />
          <div className="mt-4 font-semibold">{t("dash.empty.title")}</div>
          <div className="text-sm text-zinc-500 mt-1">{t("dash.empty.sub")}</div>
          <Link to="/projects/new" data-testid="empty-new-project-btn" className="btn-primary inline-flex items-center gap-2 mt-6 px-4 py-2">
            <FolderSimple size={16} /> {t("dash.empty.cta")}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {projects.map((p) => {
            const productQuery = p.device_id ? `&device_id=${encodeURIComponent(p.device_id)}` : "";
            return (
            <div key={p.id} className="grid-card p-5" data-testid={`project-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-mono text-[10px] text-zinc-400 uppercase tracking-wider">{t("pd.project")} · {p.id.slice(0, 8)}</div>
              <Link to={`/projects/${p.id}?section=overview${productQuery}`} className="text-xl font-extrabold mt-1 block hover:text-blue-700 transition-colors" data-testid={`project-link-${p.id}`}>
                    {p.name}
                  </Link>
                    <div className="text-sm text-zinc-600 mt-1">
                      {p.device_count ?? 0} {t((p.device_count ?? 0) === 1 ? "dash.device_one" : "dash.device_many")}
                    </div>
                </div>
                <button onClick={() => remove(p.id)} data-testid={`project-delete-${p.id}`}
                  className="p-1.5 hover:bg-red-50 hover:text-red-600 rounded-sm text-zinc-400 transition-colors">
                  <Trash size={16} />
                </button>
              </div>
              <div className="border-t border-zinc-100 mt-4 pt-3 flex items-center justify-between text-xs font-mono text-zinc-500">
                <span>v{p.current_version || 0}</span>
                <span>{new Date(p.created_at).toISOString().slice(0, 10)}</span>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-2 border-t border-[var(--border)] pt-3 sm:grid-cols-3">
                <Link
                  to={`/projects/${p.id}?section=classification${productQuery}`}
                  data-testid={`project-classification-${p.id}`}
                  className="inline-flex min-w-0 items-center justify-between gap-2 rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-xs font-semibold text-[var(--text)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--surface-hover)]"
                >
                  <span>{t("dash.open_classification")}</span>
                  <ArrowRight size={14} weight="bold" className="shrink-0 text-[var(--accent-text)]" />
                </Link>
                <Link
                  to={`/projects/${p.id}?section=documents${productQuery}`}
                  data-testid={`project-preregistration-${p.id}`}
                  className="inline-flex min-w-0 items-center justify-between gap-2 rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-xs font-semibold text-[var(--text)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--surface-hover)]"
                >
                  <span>{t("dash.open_preregistration")}</span>
                  <ArrowRight size={14} weight="bold" className="shrink-0 text-[var(--accent-text)]" />
                </Link>
                <Link
                  to={`/projects/${p.id}?section=traceability${productQuery}`}
                  data-testid={`project-postmarket-${p.id}`}
                  className="inline-flex min-w-0 items-center justify-between gap-2 rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-xs font-semibold text-[var(--text)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--surface-hover)]"
                >
                  <span>{t("dash.open_postmarket")}</span>
                  <ArrowRight size={14} weight="bold" className="shrink-0 text-[var(--accent-text)]" />
                </Link>
              </div>
            </div>
          )})}
        </div>
      )}
      {limitNoticeOpen && (
        <ProjectLimitNotice
          planTier={planTier}
          current={projects.length}
          limit={projectLimit}
          onClose={() => setLimitNoticeOpen(false)}
        />
      )}
    </AppShell>
  );
}
